import { pageTitle } from '@palaccount/config';
import type { PreviewLineResponse } from '@palaccount/contracts';
import { Link2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Hours, Ltr, Pct } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Gated, GatedGroup } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TableCell } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Modal } from '@/features/inventory/modal';
import { Typeahead } from '@/components/ui/typeahead';
import { useCan } from '@/lib/permissions';
import { usePagedState, usePageRange } from '@/lib/paging';
import { sumAmounts } from '@/lib/money';
import { localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import {
  useBillMilestone,
  useBillProgress,
  useBudget,
  useGenerateInvoice,
  useHistory,
  usePreview,
  useProject,
  useSetRateCard,
  useUnbilledAll,
} from './api';
import { useFirstProject, useProjectPicker } from './project-picker';
import { ProjectsTabs } from './projects-tabs';

/**
 * PRJ-04 — Project Billing: approved + billable + unbilled hours (and
 * completed milestones) become REAL sales invoices.
 *
 * ONE rate resolution — the project's configured method (BR-PRJ-04-05):
 * the preview simply draws the server's answer; there is no client-side
 * pricing. Exclusions require a reason, last one run, never touch the
 * timesheet (BR-PRJ-04-06) and are ALWAYS honoured — the generate request
 * carries them, and the server bills the complement (BR-PRJ-04-07).
 * Changing project clears them (BR-PRJ-04-08). Generating links straight
 * to the invoice in the Sales module (BR-PRJ-04-15). Budget: consumed =
 * billed + unbilled approved value, amber at 80%, red at 100%
 * (BR-PRJ-04-10/11). Progress billing bills only the difference
 * (BR-PRJ-04-12), Fixed Price / Retainer only (BR-PRJ-04-13).
 */

interface Exclusion {
  entryId: string;
  reason: string;
}

/** §5f's history is a compact list beneath the billing blocks, not a table. */
const HISTORY_PAGE_SIZE = 25;

const BILLING_TYPE_KEYS: Record<string, string> = {
  time_materials: 'prj.btTm',
  fixed_price: 'prj.btFixed',
  milestone: 'prj.btMilestone',
  retainer: 'prj.btRetainer',
};

export function BillingPage() {
  const { t } = useTranslation();
  const canConfigure = useCan('projects.edit');
  const [projectId, setProjectId] = useState<string>();
  const [exclusions, setExclusions] = useState<Exclusion[]>([]);
  const [excluding, setExcluding] = useState<PreviewLineResponse>();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [generated, setGenerated] = useState<{ invoiceId: string; invoiceCode: string }>();
  const [progressPct, setProgressPct] = useState('');

  useEffect(() => {
    document.title = pageTitle(t('prj.billingTitle'));
  }, [t]);

  // GEN-50 + NFR-42 — the selector searches instead of holding the portfolio,
  // and the screen's opening project is ONE row rather than the first of a
  // whole-catalogue read that priced every project on the way past.
  const { data: firstProject } = useFirstProject();
  const effectiveProjectId = projectId ?? firstProject?.id;
  const projectPicker = useProjectPicker(effectiveProjectId ?? null);
  const { data: project } = useProject(effectiveProjectId);
  const { data: preview } = usePreview(effectiveProjectId);
  const { data: budget } = useBudget(effectiveProjectId);
  const { data: unbilled } = useUnbilledAll();
  // NFR-42: the only paged list on this screen, so it keeps the default
  // `?page=` (PAGINATION.md §3 step 12 — a name only earns its place when a
  // second list would otherwise move with the first).
  const historyPaging = usePagedState(HISTORY_PAGE_SIZE);
  const { data: historyPage } = useHistory(
    effectiveProjectId,
    historyPaging.page,
    HISTORY_PAGE_SIZE,
  );
  const history = historyPage?.batches;
  const historyRange = usePageRange(historyPage, HISTORY_PAGE_SIZE);
  const generate = useGenerateInvoice();
  const billMilestone = useBillMilestone();
  const billProgress = useBillProgress();
  const setRateCard = useSetRateCard();

  const act = (promise: Promise<unknown>) => {
    setError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized, a request that never answered gets this screen's own.
      setError(localizeServerCause(t, cause, 'prj.errSaveFailedBilling'));
    });
  };

  const excludedIds = useMemo(() => new Set(exclusions.map((row) => row.entryId)), [exclusions]);
  const includedLines = (preview?.lines ?? []).filter((line) => !excludedIds.has(line.entryId));
  // The figure that becomes the draft invoice — decimal, like the invoice.
  const includedTotal = sumAmounts(includedLines.map((line) => line.amount));
  /*
   * HOURS, not an amount: PRJ-04 bills hours at a rate, and this column is the
   * duration the server multiplies. It is shown as a count of hours and is
   * never rendered as money — the money beside it is `includedTotal` above.
   */
  // eslint-disable-next-line money/no-raw-money-arithmetic -- hours, not an amount
  const includedHours = includedLines.reduce((sum, line) => sum + Number.parseFloat(line.hours), 0);

  const progressOffered =
    project !== undefined &&
    (project.billingType === 'fixed_price' || project.billingType === 'retainer');

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('prj.eyebrow')}
        </p>
        <h1 className="text-2xl font-semibold">{t('prj.billingTitle')}</h1>
      </div>
      <ProjectsTabs />

      {error !== '' ? (
        <p role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </p>
      ) : null}
      {generated !== undefined ? (
        <p role="status" className="flex items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300">
          <Link2 className="h-4 w-4" />
          {t('prj.generatedNotice')}{' '}
          <Link className="font-semibold underline" to={`/sales/${generated.invoiceId}`}>
            <Ltr value={generated.invoiceCode} />
          </Link>
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Typeahead
          label={t('prj.projectSelector')}
          value={effectiveProjectId ?? null}
          onChange={(id) => {
            setProjectId(id ?? undefined);
            // BR-PRJ-04-08: changing project clears every exclusion.
            //
            // ERR-PRJ-04-03 — *"changed project and lost their exclusions"* —
            // is this line, and its remedy column is "redo them". An exclusion
            // names ONE entry on ONE project's preview; carrying the list
            // across a project switch would leave entry ids that the new
            // preview cannot contain, so the excluded set would silently mean
            // nothing while still looking applied. Clearing is the honest
            // answer. The other half of "one run on one project" is the reset
            // after generating, below.
            setExclusions([]);
            setGenerated(undefined);
          }}
          options={projectPicker.options}
          selected={projectPicker.optionFor(effectiveProjectId)}
          query={projectPicker.query}
          onQueryChange={projectPicker.setQuery}
          emptyLabel={t('picker.noMatchingProject')}
          loading={projectPicker.loading}
          className="w-64"
        />
        {project ? (
          <>
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800 dark:bg-blue-950 dark:text-blue-300">
              {t(BILLING_TYPE_KEYS[project.billingType] ?? 'prj.btTm')}
            </span>
            <span className="text-sm text-muted-foreground">
              {project.currency} · <Amount value={project.budget} currency={project.currency} />
            </span>
          </>
        ) : null}
      </div>

      {project ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h2 className="text-sm font-semibold">{t('prj.configTitle')}</h2>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span>
                {t('prj.fBillingType')}: {t(BILLING_TYPE_KEYS[project.billingType] ?? 'prj.btTm')}
              </span>
              <span>
                {t('prj.contractValue')}: <Amount value={project.budget} currency={project.currency} />
              </span>
              {project.billingType !== 'time_materials' ? (
                <span>
                  {t('prj.fFlatRate')}: <Amount value={project.flatRate} currency={project.currency} />
                </span>
              ) : null}
            </div>
            {project.billingType === 'time_materials' ? (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('prj.rateTable')}</p>
                <div className="flex flex-wrap gap-2">
                  {project.rateCard.map((row) => (
                    <RateChip
                      key={row.id}
                      roleName={row.roleName}
                      rate={row.hourlyRate}
                      currency={project.currency}
                      editable={canConfigure}
                      onSave={(rate) =>
                        act(
                          setRateCard.mutateAsync({
                            projectId: project.id,
                            body: {
                              roles: project.rateCard.map((existing) => ({
                                roleName: existing.roleName,
                                hourlyRate:
                                  existing.roleName === row.roleName ? rate : existing.hourlyRate,
                              })),
                            },
                          }),
                        )
                      }
                    />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">{t('prj.rateHint')}</p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* The invoice preview — §5d's four conditions, priced by the server. */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div>
            <h2 className="text-sm font-semibold">{t('prj.previewTitle')}</h2>
            <p className="text-xs text-muted-foreground">{t('prj.previewSub')}</p>
          </div>
          {(preview?.lines ?? []).length === 0 ? (
            <p className="text-sm text-emerald-700 dark:text-emerald-400">{t('prj.allInvoiced')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="p-2 text-start">{t('common.date')}</th>
                  <th className="p-2 text-start">{t('prj.colEmployee')}</th>
                  <th className="p-2 text-start">{t('prj.colRole')}</th>
                  <th className="p-2 text-start">{t('prj.colTask')}</th>
                  <th className="p-2 text-end">{t('prj.colHours')}</th>
                  <th className="p-2 text-end">{t('prj.colRate')}</th>
                  <th className="p-2 text-end">{t('prj.colAmount')}</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {(preview?.lines ?? []).map((line) => {
                  const excluded = excludedIds.has(line.entryId);
                  const exclusion = exclusions.find((row) => row.entryId === line.entryId);
                  return (
                    <tr
                      key={line.entryId}
                      className={cn('border-b last:border-0', excluded && 'opacity-50')}
                    >
                      <TableCell className="px-2">
                        <Ltr value={line.workDate} />
                      </TableCell>
                      <TableCell className="px-2">{line.employeeName ?? '—'}</TableCell>
                      <TableCell className="px-2">{line.rateRole}</TableCell>
                      <TableCell className="px-2">{line.taskTitle}</TableCell>
                      <TableCell className="px-2 text-end tabular-nums">
                        <Ltr value={line.hours} />
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        <Amount value={line.rate} currency={preview?.currency} />
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        <Amount value={line.amount} currency={preview?.currency} />
                      </TableCell>
                      <TableCell className="px-2 text-end">
                        {excluded ? (
                          <span title={exclusion?.reason}>
                            <span className="me-2 text-xs font-semibold text-muted-foreground">
                              {t('prj.excluded')}
                            </span>
                            <Gated permission="projects.approve" reasonKey="perm.cannot.billProjects">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setExclusions((current) =>
                                    current.filter((row) => row.entryId !== line.entryId),
                                  )
                                }
                              >
                                {t('prj.restore')}
                              </Button>
                            </Gated>
                          </span>
                        ) : (
                          <Gated permission="projects.approve" reasonKey="perm.cannot.billProjects">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setReason('');
                              setExcluding(line);
                            }}
                          >
                            {t('prj.exclude')}
                          </Button>
                          </Gated>
                        )}
                      </TableCell>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {(preview?.lines ?? []).length > 0 ? (
            <div className="flex items-center justify-between border-t pt-3 text-sm">
              <span className="text-muted-foreground">
                {t('prj.includedCount', { count: includedLines.length })} ·{' '}
                <Hours value={includedHours.toFixed(2)} />
              </span>
              <span className="flex items-center gap-3">
                <span className="font-semibold">
                  {t('prj.total')} <Amount value={includedTotal} currency={preview?.currency} />
                </span>
                <Gated permission="projects.approve" reasonKey="perm.cannot.billProjects">
                  <Button
                    disabled={includedLines.length === 0 || generate.isPending}
                    onClick={() => {
                      const projectToBill = effectiveProjectId!;
                      const payload = exclusions;
                      act(
                        generate
                          .mutateAsync({
                            projectId: projectToBill,
                            body: payload.length > 0 ? { exclusions: payload } : {},
                          })
                          .then((result) => {
                            // Exclusions last ONE run (BR-PRJ-04-06).
                            setExclusions([]);
                            setGenerated({
                              invoiceId: result.invoiceId,
                              invoiceCode: result.invoiceCode,
                            });
                          }),
                      );
                    }}
                  >
                    {t('prj.generateInvoice')}
                  </Button>
                </Gated>
              </span>
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">{t('prj.generateHint')}</p>
        </CardContent>
      </Card>

      {/* Milestones — ONE list; only completed can be invoiced (§5f). */}
      {project && project.milestones.length > 0 ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h2 className="text-sm font-semibold">{t('prj.milestones')}</h2>
            <ul className="space-y-2">
              {project.milestones.map((milestone) => (
                <li key={milestone.id} className="flex items-center justify-between text-sm">
                  <span>
                    {milestone.name}
                    <span className="ms-2 text-xs text-muted-foreground">
                      <Ltr value={milestone.dueDate} /> · <Amount value={milestone.amount} currency={project.currency} />
                    </span>
                  </span>
                  {milestone.status === 'invoiced' ? (
                    <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                      {milestone.invoiceCode !== null ? <Ltr value={milestone.invoiceCode} /> : null}
                    </span>
                  ) : milestone.status === 'completed' ? (
                    <Gated permission="projects.approve" reasonKey="perm.cannot.billProjects">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        act(
                          billMilestone.mutateAsync(milestone.id).then((result) => {
                            setGenerated({
                              invoiceId: result.invoiceId,
                              invoiceCode: result.invoiceCode,
                            });
                          }),
                        )
                      }
                    >
                      {t('prj.generateInvoice')}
                    </Button>
                    </Gated>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      {t(`prj.ms${milestone.status}`)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {/* Budget — consumed = billed + unbilled approved (§5g). */}
      {budget ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <h2 className="text-sm font-semibold">{t('prj.budgetTitle')}</h2>
            {budget.alert !== null ? (
              <p
                className={cn(
                  'rounded-md px-3 py-2 text-sm font-medium',
                  budget.alert === 'amber'
                    ? 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
                )}
              >
                {budget.alert === 'amber' ? t('prj.overEighty') : t('prj.overHundred')}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-4 text-sm">
              <span>
                {t('prj.billed')}: <Amount value={budget.invoicedTotal} currency={project?.currency} />
              </span>
              <span>
                {t('prj.unbilledValue')}: <Amount value={budget.unbilledApprovedValue} currency={project?.currency} />
              </span>
              <span className="font-semibold">
                {t('prj.consumed')}: <Amount value={budget.consumed} currency={project?.currency} /> (
                <Pct value={budget.consumedPct} />)
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{t('prj.consumedFormula')}</p>
            {progressOffered ? (
              <GatedGroup permission="projects.approve" reasonKey="perm.cannot.billProjects">
              <div className="flex items-end gap-2 pt-2">
                <Input
                  aria-label={t('prj.progressPct')}
                  placeholder={t('prj.progressPct')}
                  inputMode="decimal"
                  value={progressPct}
                  onChange={(event) => setProgressPct(event.target.value)}
                  className="w-24"
                />
                <span className="pb-2 text-xs text-muted-foreground">
                  {t('prj.alreadyBilled', { pct: budget.billedProgressPct })}
                </span>
                <Button
                  variant="outline"
                  disabled={progressPct === ''}
                  onClick={() =>
                    act(
                      billProgress
                        .mutateAsync({ projectId: effectiveProjectId!, toPct: progressPct })
                        .then((result) => {
                          setProgressPct('');
                          setGenerated({
                            invoiceId: result.invoiceId,
                            invoiceCode: result.invoiceCode,
                          });
                        }),
                    )
                  }
                >
                  {t('prj.billToPct')}
                </Button>
              </div>
              </GatedGroup>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {/* Unbilled time — all projects. */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <h2 className="text-sm font-semibold">{t('prj.unbilledTitle')}</h2>
          <p className="text-xs text-muted-foreground">{t('prj.unbilledSub')}</p>
          {(unbilled ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('prj.unbilledNone')}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {(unbilled ?? []).map((row) => (
                <li
                  key={row.projectId}
                  className={cn(
                    'flex justify-between rounded px-2 py-1',
                    row.projectId === effectiveProjectId && 'bg-amber-50 dark:bg-amber-950/40',
                  )}
                >
                  <span>
                    <Ltr value={row.code} /> {row.name}
                  </span>
                  <span className="tabular-nums text-amber-700 dark:text-amber-400">
                    <Hours value={row.hours} /> · <Amount value={row.value} currency={row.currency} />
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Invoice history — every run opens its REAL invoice in Sales. */}
      <Card>
        <CardContent className="space-y-2 p-4">
          <h2 className="text-sm font-semibold">{t('prj.historyTitle')}</h2>
          {(history ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('prj.historyNone')}</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {(history ?? []).map((batch) => (
                <li key={batch.id} className="flex items-center justify-between">
                  <span>
                    <Link className="font-medium underline" to={`/sales/${batch.invoiceId}`}>
                      <Ltr value={batch.invoiceCode} />
                    </Link>
                    <span className="ms-2 text-xs uppercase text-muted-foreground">
                      {t(`prj.bk${batch.kind}`)}
                    </span>
                  </span>
                  <span className="tabular-nums">
                    <Amount value={batch.amount} currency={project?.currency} />
                  </span>
                </li>
              ))}
            </ul>
          )}
          {historyRange.total > 0 ? (
            <Pager
              page={historyPaging.page}
              pages={historyRange.pages}
              first={historyRange.first}
              last={historyRange.last}
              total={historyRange.total}
              onPage={historyPaging.setPage}
              label={t('prj.historyTitle')}
            />
          ) : null}
        </CardContent>
      </Card>

      {excluding !== undefined ? (
        <Modal title={t('prj.excludeTitle')} onClose={() => setExcluding(undefined)}>
          {/* §5e: the timesheet entry is untouched. */}
          <p className="text-sm text-muted-foreground">{t('prj.excludeSub')}</p>
          <Textarea
            aria-label={t('prj.excludeReason')}
            placeholder={t('prj.excludeReason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            className="mt-2"
          />
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setExcluding(undefined)}>
              {t('common.cancel')}
            </Button>
            {/* Visibly disabled without a reason (the silent-nothing defect). */}
            <Button
              disabled={reason.trim() === ''}
              onClick={() => {
                const line = excluding;
                setExcluding(undefined);
                setExclusions((current) => [
                  ...current,
                  { entryId: line.entryId, reason: reason.trim() },
                ]);
              }}
            >
              {t('prj.excludeDo')}
            </Button>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

function RateChip({
  roleName,
  rate,
  currency,
  editable,
  onSave,
}: {
  roleName: string;
  rate: string;
  currency: string;
  editable: boolean;
  onSave: (rate: string) => void;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(rate);

  if (!editing) {
    return (
      <button
        type="button"
        disabled={!editable}
        onClick={() => {
          setValue(rate);
          setEditing(true);
        }}
        className="rounded-full border px-3 py-1 text-xs"
        aria-label={t('prj.editRate', { role: roleName })}
      >
        {roleName} <Amount value={rate} currency={currency} className="font-semibold" />
      </button>
    );
  }
  return (
    <span className="flex items-center gap-1">
      <span className="text-xs">{roleName}</span>
      <Input
        aria-label={t('prj.editRate', { role: roleName })}
        inputMode="decimal"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        className="h-7 w-20"
      />
      <Button
        size="sm"
        variant="outline"
        onClick={() => {
          setEditing(false);
          onSave(value);
        }}
      >
        {t('common.save')}
      </Button>
    </span>
  );
}
